import React, { useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import {
  ActivityItem,
  ActivityResponseDto,
  ActivityType,
} from "@/modules/lead-page/dtos/index-v2";
import { ActivityContext } from "@/modules/lead-page/new/hooks/activity-context.hook";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import { ActivityContextType } from "@/modules/lead-page/new/context/activity.context";
import { getTrackActivityTimeline } from "../api";
import {
  TrackStatusUpdates,
  TrackCommentSourceEnum,
  TrackCommentUpdates,
  TrackAssignmentUpdates,
  TrackUpdateDto,
  TrackUpdates,
} from "../dtos/track-activity";
import useDeleteComments from "../hooks/comments/delete-comment.hook";

export const TrackActivityProvider: React.FC<{
  children: React.ReactNode;
  trackUId: string;
}> = ({ children, trackUId }) => {
  const { isMobile } = useScreenSize();

  const [type, setType] = useState<ActivityType[]>([
    ActivityType.Comment,
    ActivityType.StatusUpdate,
    ActivityType.AssignmentUpdate,
  ]);

  const [mobileQueryVersion, setMobileQueryVersion] = useState(0);

  const queryKey = useMemo(
    () =>
      isMobile
        ? ["track", "activity", trackUId, mobileQueryVersion]
        : ["track", "activity", trackUId, type],
    [trackUId, type, isMobile, mobileQueryVersion]
  );

  const forceRefetch = () => {
    if (isMobile) {
      setMobileQueryVersion((v) => v + 1);
    } else {
      refetch();
    }
  };

  const {
    data,
    fetchNextPage,
    hasNextPage = false,
    isFetchingNextPage = false,
    isLoading = false,
    isError = false,
    isFetching,
    refetch,
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }) =>
      getTrackActivityTimeline({
        trackUid: trackUId,
        limit: 20,
        filter: type,
        cursorDate: pageParam,
      }),
    select: (data) => {
      return {
        pages: data.pages.map((page) =>
          mapTrackUpdateResponseToActivityResponse(page.data)
        ),
        pageParams: data.pageParams,
      };
    },
    getNextPageParam: (lastPage) => lastPage.data.nextCursor ?? undefined,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const filters = useMemo(
    () => ({
      type,
      setType,
    }),
    [type]
  );

  const [deletingId, setDeletingId] = useState<number | string | undefined>();

  const {
    handleDeleteComments,
    setDialogOpenConfirm,
    dialogOpenConfirm,
    loadDeleting,
  } = useDeleteComments({
    trackUId,
  });

  const value: ActivityContextType = {
    filters,
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isLoading,
    isError,
    forceRefetch,
    handleDeleteComments: () => {
      handleDeleteComments(deletingId);
      setDeletingId(undefined);
    },
    setDialogOpenConfirm,
    dialogOpenConfirm,
    loadDeleting,
    deletingId,
    setDeletingId,
    highlightedActivityId: undefined,
    setHighlightedActivityId: () => {},
  };

  return (
    <ActivityContext.Provider value={value}>
      {children}
    </ActivityContext.Provider>
  );
};

// helper to normalize `addedBy`
function getActor(addedBy?: { id: number; name: string; color?: string }) {
  return {
    actorName: addedBy?.name ?? null,
    actorColor: addedBy?.color ?? null,
  };
}

function mapTrackUpdateResponseToActivityResponse(
  input: TrackUpdateDto
): ActivityResponseDto {
  const items: ActivityItem[] = input.data.map((update) =>
    mapTrackUpdateToActivityItem(update)
  );

  return {
    data: {
      items,
      count: items.length,
      nextCursor: input.nextCursor ?? "",
    },
  };
}

function mapTrackUpdateToActivityItem(update: TrackUpdates): ActivityItem {
  switch (update.type) {
    case "comment":
      return mapCommentUpdate(update);
    case "status_update":
      return mapStatusUpdate(update);
    case "assignment_update":
      return mapAssignmentUpdate(update);
    default:
      throw new Error(`Unknown update type: ${(update as any).type}`);
  }
}

function mapCommentUpdate(update: TrackCommentUpdates): ActivityItem {
  const { actorName, actorColor } = getActor(update.addedBy);
  const isRespondIO = update.commentSource === TrackCommentSourceEnum.respondIO;
  const isFollowUp =
    update.commentSource === TrackCommentSourceEnum.trackFollowUp;

  return {
    type: isRespondIO
      ? ActivityType.RespondIOSessionSummary
      : ActivityType.Comment,
    id: update.id,
    uid: update.uid as any,
    currentJourneyId: 0,
    oldJourneyId: null,
    oldJourneyNanoId: null,
    formData: isFollowUp ? { tag: "Follow Up" } : {},
    content: update.data.comment,
    actorName,
    actorColor,
    previousUser: null,
    previousUserColor: null,
    newUser: null,
    newUserColor: null,
    previousStatus: null,
    previousStatusColor: null,
    newStatus: null,
    newStatusColor: null,
    assignmentType: null,
    mentions: [],
    createdAt: update.createdAt,
    deletedAt: null,
    entityType: "Track",
  };
}

function mapStatusUpdate(update: TrackStatusUpdates): ActivityItem {
  const { actorName, actorColor } = getActor(update.addedBy);

  return {
    type: ActivityType.StatusUpdate,
    id: update.id,
    uid: "" as any,
    currentJourneyId: 0,
    oldJourneyId: null,
    oldJourneyNanoId: null,
    formData: {},
    content: update.comment ?? null,
    actorName,
    actorColor,
    previousUser: null,
    previousUserColor: null,
    newUser: null,
    newUserColor: null,
    previousStatus: update.data.oldStatus?.name ?? null,
    previousStatusColor: update.data.oldStatus?.color ?? null,
    newStatus: update.data.newStatus?.name ?? null,
    newStatusColor: update.data.newStatus?.color ?? null,
    assignmentType: null,
    mentions: [],
    createdAt: update.createdAt,
    deletedAt: null,
    entityType: "Track",
  };
}

function mapAssignmentUpdate(update: TrackAssignmentUpdates): ActivityItem {
  const { actorName, actorColor } = getActor(update.addedBy);

  return {
    type: ActivityType.AssignmentUpdate,
    id: update.id,
    uid: "" as any,
    oldJourneyNanoId: null,
    formData: {},
    currentJourneyId: 0,
    oldJourneyId: null,
    content: update.comment ?? null,
    actorName,
    actorColor,
    previousUser: update.data.oldUser?.name ?? null,
    previousUserColor: update.data.oldUser?.color ?? null,
    newUser: update.data.newUser?.name ?? null,
    newUserColor: update.data.newUser?.color ?? null,
    previousStatus: null,
    previousStatusColor: null,
    newStatus: null,
    newStatusColor: null,
    assignmentType: null,
    mentions: [],
    createdAt: update.createdAt,
    deletedAt: null,
    entityType: "Track",
  };
}
